import { useState, useEffect } from "react";
import api from "@/services/api";
import {
  Building2,
  User,
  Mail,
  Phone,
  Briefcase,
  Euro,
  FileText,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Link2,
  Plus,
  Search,
  Loader2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

// Servicios SRS mapping
// Opciones de feedback al convertir — ¿qué te convenció?
const FEEDBACK_CONVERSION_OPTIONS = [
  { value: "score_acertado", label: "Score alto acertado" },
  { value: "pain_relevante", label: "Análisis de Pain relevante" },
  { value: "pliego_compatible", label: "Pliego con requisitos compatibles" },
  { value: "adjudicatario_conocido", label: "Adjudicatario conocido / buen contacto" },
  { value: "sector_estrategico", label: "Sector estratégico" },
  { value: "importe_atractivo", label: "Importe atractivo" },
];

const SERVICIOS_POR_TIPO = {
  "Soporte IT / Helpdesk": ["Soporte IT Remoto", "Field Services", "Help Desk"],
  "Cableado Estructurado": ["Cableado Cat6/Fibra", "Certificación Redes"],
  "Infraestructura / CPD": ["Instalación CPD", "Mantenimiento Infraestructura"],
  "Cloud & Virtualización": ["Migración Cloud", "VMware/Virtualización"],
  "Ciberseguridad / ENS": ["Auditoría Seguridad", "Consultoría ENS/ISO27001"],
  "Comunicaciones Unificadas": ["VoIP/Telefonía", "Videoconferencia"],
  "Healthcare IT": ["Integración HIS/RIS", "Soporte Sanitario"],
  "Fotovoltaica / Energía": ["Inspección FV Drones", "Mantenimiento Solar"],
  "Drones / Cartografía": ["Drones Inspección", "Topografía/Fotogrametría"],
  "Consultoría Técnica": ["Consultoría IT", "Diseño Arquitectura"],
  "Refuerzo Ejecución": ["Staff Augmentation", "Proyectos Llave en Mano"],
};

export default function ConvertirLeadModal({ open, onClose, oportunidad, adjudicatarioData, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("crear"); // "crear" | "vincular"
  const [searchResults, setSearchResults] = useState([]);
  const [selectedExistingLead, setSelectedExistingLead] = useState(null);
  const [searching, setSearching] = useState(false);

  const [contactosSugeridos, setContactosSugeridos] = useState([]);
  const [contactoAplicado, setContactoAplicado] = useState(false);
  const [formData, setFormData] = useState({
    contacto: "",
    email: "",
    telefono: "",
    cargo: "",
    servicios: [],
    valor_estimado: 0,
    notas: "",
    feedback_conversion: [],
  });

  // Reset mode when modal opens + search contactos for auto-fill
  useEffect(() => {
    if (open) {
      setMode("crear");
      setSelectedExistingLead(null);
      setSearchResults([]);
      setContactosSugeridos([]);
      setContactoAplicado(false);

      // Search existing contacts by adjudicatario
      if (oportunidad?.adjudicatario) {
        api
          .get('/contactos/search', {
            params: { q: oportunidad.adjudicatario },
          })
          .then((r) => setContactosSugeridos(r.data || []))
          .catch(() => {});
      }
    }
  }, [open, oportunidad?.adjudicatario]);

  // Auto-search leads when switching to vincular mode
  const searchLeads = async (empresa) => {
    if (!empresa) return;
    setSearching(true);
    try {
      const resp = await api.get(
        '/leads/search',
        { params: { empresa } }
      );
      setSearchResults(resp.data || []);
    } catch (e) {
      console.error("Error buscando leads:", e);
      toast.error("Error buscando leads");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (mode === "vincular" && oportunidad?.adjudicatario) {
      searchLeads(oportunidad.adjudicatario);
    }
  }, [mode, oportunidad?.adjudicatario]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch fresh data and initialize form when modal opens
  useEffect(() => {
    if (!oportunidad || !open) return;

    const initForm = async () => {
      const valorSugerido = Math.round((oportunidad.importe || 0) * 0.2);
      const serviciosSugeridos = SERVICIOS_POR_TIPO[oportunidad.tipo_srs] || [];

      // Fetch fresh adjudicatario_data from backend
      let adj = adjudicatarioData || oportunidad.adjudicatario_data || {};
      try {
        const resp = await api.get(
          `/oportunidades/${oportunidad.oportunidad_id}`
        );
        if (resp.data?.adjudicatario_data) {
          adj = resp.data.adjudicatario_data;
        }
      } catch (e) {
        // Fallback to props — already set above
      }

      setFormData({
        contacto: adj.persona_contacto || "",
        email: adj.email || "",
        telefono: adj.telefono || "",
        cargo: adj.cargo_contacto || adj.departamento || "",
        servicios: serviciosSugeridos,
        valor_estimado: valorSugerido,
        notas: `Lead creado desde SpotterSRS\nContrato: ${oportunidad.titulo}\nImporte contrato: €${(oportunidad.importe || 0).toLocaleString("es-ES")}`,
        feedback_conversion: [],
      });
    };

    initForm();
  }, [oportunidad, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleServicio = (servicio) => {
    setFormData((prev) => ({
      ...prev,
      servicios: prev.servicios.includes(servicio)
        ? prev.servicios.filter((s) => s !== servicio)
        : [...prev.servicios, servicio],
    }));
  };

  const toggleFeedback = (value) => {
    setFormData((prev) => ({
      ...prev,
      feedback_conversion: prev.feedback_conversion.includes(value)
        ? prev.feedback_conversion.filter((f) => f !== value)
        : [...prev.feedback_conversion, value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === "vincular") {
      if (!selectedExistingLead) {
        toast.error("Selecciona un lead existente");
        return;
      }
    } else {
      if (!formData.contacto.trim()) {
        toast.error("El nombre del contacto es obligatorio");
        return;
      }
      if (!formData.email.trim()) {
        toast.error("El email es obligatorio");
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        ...(mode === "vincular" && selectedExistingLead
          ? { existing_lead_id: selectedExistingLead.lead_id }
          : {}),
      };

      await api.post(
        `/oportunidades/${oportunidad.oportunidad_id}/convertir`,
        payload
      );

      toast.success(
        mode === "vincular"
          ? "Oportunidad vinculada al lead existente"
          : "Lead creado exitosamente"
      );
      onSuccess?.();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al crear lead";
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  if (!oportunidad) return null;

  // Get all possible services for this tipo - use reduce for safer flattening
  const serviciosDisponibles = SERVICIOS_POR_TIPO[oportunidad.tipo_srs] ||
    Object.values(SERVICIOS_POR_TIPO).reduce((acc, val) => acc.concat(val), []).slice(0, 6);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-white/10 !max-w-[640px] w-[95vw] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-white/10 shrink-0">
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-cyan-400" />
            Convertir a Lead
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-4">
            {/* Oportunidad summary */}
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white break-words">{oportunidad.adjudicatario}</p>
                  <p className="text-xs text-slate-400 truncate">{oportunidad.titulo}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="border-green-500/30 text-green-400 text-xs whitespace-nowrap">
                      {formatCurrency(oportunidad.importe)}
                    </Badge>
                    {oportunidad.tipo_srs && (
                      <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs whitespace-nowrap">
                        {oportunidad.tipo_srs}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mode Toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("crear")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === "crear"
                    ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                    : "bg-slate-800/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
                }`}
              >
                <Plus className="w-4 h-4" />
                Crear nuevo lead
              </button>
              <button
                type="button"
                onClick={() => setMode("vincular")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === "vincular"
                    ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
                    : "bg-slate-800/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
                }`}
              >
                <Link2 className="w-4 h-4" />
                Vincular a existente
              </button>
            </div>

            {mode === "crear" ? (
              <>
                {/* Alert */}
                <div className="flex items-start gap-2 p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                  <AlertCircle className="w-4 h-4 text-cyan-400 mt-0.5" />
                  <p className="text-xs text-cyan-300">
                    Completa los datos del contacto en la empresa adjudicataria.
                    La empresa y fuente se rellenan automaticamente.
                  </p>
                </div>

                {/* Auto-fill from contactos */}
                {contactosSugeridos.length > 0 && !contactoAplicado && (
                  <div className="space-y-2">
                    {contactosSugeridos.map((ct) => (
                      <div
                        key={ct._id}
                        className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-green-300">
                            {ct.nombre} — {ct.empresa}
                          </p>
                          <p className="text-xs text-green-400/70">
                            {ct.email}{ct.cargo ? ` · ${ct.cargo}` : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              contacto: ct.nombre || prev.contacto,
                              email: ct.email || prev.email,
                              telefono: ct.telefono || prev.telefono,
                              cargo: ct.cargo || prev.cargo,
                            }));
                            setContactoAplicado(true);
                            toast.success("Datos del contacto aplicados");
                          }}
                          className="ml-2 px-3 py-1.5 text-xs font-medium rounded-md bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-colors border border-green-500/30 shrink-0"
                        >
                          Usar datos
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Contact fields */}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-slate-300 text-sm">Nombre Contacto *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        value={formData.contacto}
                        onChange={(e) => handleChange("contacto", e.target.value)}
                        placeholder="Ej: Juan Garcia"
                        className="bg-slate-950 border-slate-800 h-10 text-base pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300 text-sm">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="Ej: jgarcia@empresa.com"
                        className="bg-slate-950 border-slate-800 h-10 text-base pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300 text-sm">Telefono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        value={formData.telefono}
                        onChange={(e) => handleChange("telefono", e.target.value)}
                        placeholder="Ej: +34 600 000 000"
                        className="bg-slate-950 border-slate-800 h-10 text-base pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-300 text-sm">Cargo</Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        value={formData.cargo}
                        onChange={(e) => handleChange("cargo", e.target.value)}
                        placeholder="Ej: Director de Operaciones"
                        className="bg-slate-950 border-slate-800 h-10 text-base pl-10"
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Vincular mode — search existing leads */}
                <div className="flex items-start gap-2 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <Link2 className="w-4 h-4 text-purple-400 mt-0.5" />
                  <p className="text-xs text-purple-300">
                    Vincula esta oportunidad a un lead existente.
                    Se sumara el valor estimado y se registrara la actividad.
                  </p>
                </div>

                {/* Search bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por empresa..."
                    defaultValue={oportunidad?.adjudicatario || ""}
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      if (val.length >= 2) searchLeads(val);
                    }}
                    className="bg-slate-950 border-slate-800 h-10 text-base pl-10"
                  />
                </div>

                {/* Results */}
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {searching ? (
                    <div className="flex items-center justify-center gap-2 py-4 text-slate-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Buscando...</span>
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-center py-4 text-slate-500 text-sm">
                      No se encontraron leads con esa empresa
                    </div>
                  ) : (
                    searchResults.map((lead) => (
                      <div
                        key={lead.lead_id}
                        onClick={() => setSelectedExistingLead(lead)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedExistingLead?.lead_id === lead.lead_id
                            ? "bg-purple-500/20 border border-purple-500/40"
                            : "bg-slate-800/50 border border-transparent hover:border-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate">
                              {lead.empresa}
                            </p>
                            <p className="text-xs text-slate-400">
                              {lead.contacto} &middot; {lead.email || "sin email"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-2 shrink-0">
                            <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
                              {lead.etapa || "nuevo"}
                            </Badge>
                            {lead.valor_estimado > 0 && (
                              <span className="text-xs text-green-400">
                                {formatCurrency(lead.valor_estimado)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Servicios de interés */}
            <div className="space-y-2 pt-2">
              <Label className="text-slate-300 text-sm font-semibold">Servicios de Interés</Label>
              <div className="grid grid-cols-2 gap-2">
                {serviciosDisponibles.map((servicio) => (
                  <div
                    key={servicio}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors border border-transparent hover:border-slate-700"
                  >
                    <Checkbox
                      checked={formData.servicios.includes(servicio)}
                      onCheckedChange={() => toggleServicio(servicio)}
                      className="border-slate-500 data-[state=checked]:bg-cyan-500 w-5 h-5 rounded"
                    />
                    <span
                      className="text-base text-slate-200 flex-1"
                      onClick={() => toggleServicio(servicio)}
                    >
                      {servicio}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Feedback: ¿Qué te convenció? */}
            <div className="space-y-2 pt-2">
              <Label className="text-slate-300 text-sm font-semibold">
                ¿Qué te convenció? (opcional)
              </Label>
              <p className="text-xs text-slate-500 -mt-1">
                Tu feedback ayuda a mejorar el scoring
              </p>
              <div className="grid grid-cols-2 gap-2">
                {FEEDBACK_CONVERSION_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors border border-transparent hover:border-slate-700"
                  >
                    <Checkbox
                      checked={formData.feedback_conversion.includes(opt.value)}
                      onCheckedChange={() => toggleFeedback(opt.value)}
                      className="border-slate-500 data-[state=checked]:bg-green-500 w-5 h-5 rounded"
                    />
                    <span
                      className="text-sm text-slate-200 flex-1"
                      onClick={() => toggleFeedback(opt.value)}
                    >
                      {opt.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Valor estimado */}
            <div className="space-y-2 pt-2">
              <Label className="text-slate-300 text-sm">
                Valor Estimado (subcontratación)
              </Label>
              <div className="relative">
                <Euro className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  value={formData.valor_estimado}
                  onChange={(e) => handleChange("valor_estimado", parseFloat(e.target.value) || 0)}
                  className="bg-slate-950 border-slate-800 h-10 text-base pl-10"
                />
              </div>
              <span className="text-xs text-slate-500 italic px-1">
                (Sugerido: 20% del contrato total)
              </span>
            </div>

            {/* Notas */}
            <div className="space-y-2 pt-2">
              <Label className="text-slate-300 text-sm">
                Notas Adicionales
              </Label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Textarea
                  value={formData.notas}
                  onChange={(e) => handleChange("notas", e.target.value)}
                  className="bg-slate-950 border-slate-800 min-h-[100px] text-base p-3 pl-10"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="p-6 pt-4 border-t border-white/10 bg-slate-900/50 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-slate-700 text-slate-300 hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700 transition"
            >
              {loading ? (
                "Procesando..."
              ) : mode === "vincular" ? (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Vincular Oportunidad
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Crear Lead
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
